// @flow
import { has } from "../../utils/helpers/objects";

import ActionCollection from "../actions/ActionCollection";
import AttributeCollection from "../attributes/AttributeCollection";
import ResourceModel from "../base/ResourceModel";

import { AVATAR, AVATAR_IN_TITLE, TITLE } from "../../constants/LayoutHints";

import type { ModularUIResponse } from "../../modularui";
import type { ModularUIModel, AttributeType } from "../types";
import type LinkModel from "../links/LinkModel";
import type ErrorResponse from "../error/ErrorResponse";

/**
 * Base class for details<br/>
 * For instance the details of case 1<br/>
 * For instance the details of record 12<br/>
 */
export default class DetailModel extends ResourceModel {
  _attributeCollection: AttributeCollection;
  _metadataCollection: AttributeCollection;
  _actionCollection: ActionCollection;

  /**
   */
  constructor(modularuiResponse: ModularUIResponse) {
    super(modularuiResponse);

    this.createAttributeCollection();
    this.createMetadataCollection();
    this.createActionCollection();
  }

  /**
   */
  get type(): string {
    return "Detail";
  }

  /**
   */
  static get modelName(): string {
    return "DetailModel";
  }

  /**
   */
  static isApplicableModel(data: ModularUIResponse): boolean {
    const resourceType = data.contributions?.resourcetype;
    return (
      resourceType &&
      (resourceType.endsWith("DetailPanel") ||
        ["Detail", "CasePropertiesPanel", "CasePropertiesPanelDetail"].includes(
          resourceType,
        ))
    );
  }

  /**
   */
  getInitialChildModelLinks(): Array<LinkModel> {
    return this._attributeCollection.getInitialChildModelLinks();
  }

  /**
   */
  setChildModels(models: Array<ModularUIModel>, errors: Array<ErrorResponse>) {
    this._attributeCollection.setChildModels(models, errors);
  }

  /**
   * Getting the unique identifier of the details
   */
  get id(): string {
    return this.getData("_id", this.key);
  }

  /**
   */
  get label(): string {
    return this.getContribution("label", "");
  }

  /**
   * Attribute collection
   */
  createAttributeCollection() {
    const attributeContributions = this.contributions.attributes
      ? this.contributions.attributes.filter((contribution) => {
          const [key] = Object.keys(contribution);
          return has(this.data, key);
        })
      : [];

    this.attributeCollection = new AttributeCollection(
      this.data,
      attributeContributions,
      { ...this.modelOptions, isReadonly: true },
    );
  }

  /**
   */
  get attributeCollection(): AttributeCollection {
    return this._attributeCollection;
  }

  /**
   */
  set attributeCollection(collection: AttributeCollection) {
    this._attributeCollection = collection;
  }

  /**
   * Retrieve list of visible attributes
   */
  get attributes(): Array<AttributeType> {
    return this.attributeCollection.all.filter((attribute) => {
      const hasTitle = attribute.layouthint.hasExact(TITLE);
      const hasAvatar = attribute.layouthint.hasExact(AVATAR);
      const hasAvatarForTitle = attribute.layouthint.hasExact(AVATAR_IN_TITLE);
      return (
        !attribute.isHidden &&
        (!hasTitle || (hasTitle && hasAvatar)) &&
        (!hasAvatarForTitle || (hasAvatarForTitle && !hasAvatar))
      );
    });
  }

  /**
   * Retrieve an attribute by it's key
   */
  getAttributeByKey(key: string): AttributeType | null {
    return this._attributeCollection.getAttributeByKey(key);
  }

  /**
   * Metadata collection
   */
  createMetadataCollection() {
    const metadataContributions = this.contributions.metadata
      ? Object.keys(this.contributions.metadata).map((metadataKey) => ({
          [metadataKey]: this.contributions.metadata[metadataKey],
        }))
      : [];

    this.metadataCollection = new AttributeCollection(
      this.data,
      metadataContributions,
      { ...this.modelOptions, isReadonly: true },
    );
  }

  /**
   */
  get metadataCollection(): AttributeCollection {
    return this._metadataCollection;
  }

  /**
   */
  set metadataCollection(collection: AttributeCollection) {
    this._metadataCollection = collection;
  }

  /**
   * Action collection
   */
  createActionCollection() {
    this._actionCollection = new ActionCollection(
      this.data.actions,
      this.contributions.actions,
      this.modelOptions,
    );
  }

  /**
   */
  get actionCollection(): ActionCollection {
    return this._actionCollection;
  }

  /**
   */
  set actionCollection(actionCollection: ActionCollection) {
    this._actionCollection = actionCollection;
  }

  /**
   * Determines if this is a case
   */
  get isCase(): boolean {
    return this.contributions.resourcetype === "CaseView";
  }

  /**
   * Getting the attribute that has as layout hint 'avatar-for-title'
   */
  get avatarForTitleAttribute(): ?AttributeType {
    return this._attributeCollection.find(
      (attribute) =>
        attribute.layouthint.hasExact(AVATAR_IN_TITLE) &&
        attribute.layouthint.hasExact(AVATAR),
    );
  }

  /**
   * Getting the attribute that has as layout hint 'title'
   */
  get titleAttribute(): ?AttributeType {
    return this._attributeCollection.find(
      (attribute) =>
        attribute.layouthint.has(TITLE) &&
        !attribute.layouthint.hasExact(AVATAR),
    );
  }

  /**
   * Update current detail with a new detail model and return a cloned version of the model
   */
  update(model: ModularUIModel): DetailModel {
    if (model instanceof DetailModel) {
      const clonedModel = this.clone();

      clonedModel.attributeCollection = model._attributeCollection;
      clonedModel.metadataCollection = model._metadataCollection;
      clonedModel.actionCollection = model._actionCollection;

      return clonedModel;
    }

    return this;
  }

  /**
   */
  equals(model: DetailModel): boolean {
    return this.id.toString() === model.id.toString();
  }
}
